import type { User } from "@supabase/supabase-js";
import { createClient } from "@lib/supabase/client";
import { resetPushSubscriptionSync } from "@features/notifications/push/push-subscription.service";
import {
  clearPendingVerifyEmail,
  getPendingVerifyEmail,
  setPendingVerifyEmail,
} from "@lib/auth/post-auth";
import { clearPasswordRecoveryPending } from "@lib/auth/password-recovery";
import {
  getOAuthRedirectUrl,
  getPasswordResetRedirectUrl,
} from "@lib/platform/app-origin";
import {
  forgotPasswordSchema,
  otpSchema,
  resetPasswordSchema,
  signInSchema,
  signUpSchema,
} from "@utils/validations/auth";

export type AuthResult<T = void> =
  | { success: true; data?: T; user?: User }
  | { success: false; error: string; code?: string };

// dummy commit

export const authService = {
  async signUp(
    input: {
      name: string;
      email: string;
      password: string;
    },
    captchaToken?: string,
  ): Promise<AuthResult> {
    const parsed = signUpSchema.safeParse(input);
    if (!parsed.success) {
      return {
        success: false,
        error: parsed.error.issues[0]?.message ?? "Invalid input",
      };
    }

    const { email, password, name } = parsed.data;
    const supabase = createClient();
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { full_name: name },
        ...(captchaToken ? { captchaToken } : {}),
      },
    });

    if (error) {
      return { success: false, error: error.message };
    }

    const existingVerified = data?.user?.identities?.length === 0;

    if (existingVerified) {
      return {
        success: false,
        error: "An account with this email already exists. Please sign in.",
        code: "EMAIL_ALREADY_VERIFIED",
      };
    }

    await setPendingVerifyEmail(email);
    return { success: true };
  },

  async signIn(
    input: {
      email: string;
      password: string;
    },
    captchaToken?: string,
  ): Promise<AuthResult<{ user: User }>> {
    const parsed = signInSchema.safeParse(input);
    if (!parsed.success) {
      return {
        success: false,
        error: parsed.error.issues[0]?.message ?? "Invalid input",
      };
    }

    const supabase = createClient();
    const { data, error } = await supabase.auth.signInWithPassword({
      ...parsed.data,
      ...(captchaToken ? { options: { captchaToken } } : {}),
    });

    if (error) {
      if (error.code === "email_not_confirmed") {
        await setPendingVerifyEmail(parsed.data.email);
        return {
          success: false,
          error: "EMAIL_NOT_CONFIRMED",
          code: "EMAIL_NOT_CONFIRMED",
        };
      }
      return { success: false, error: error.message };
    }

    return { success: true, data: { user: data.user }, user: data.user };
  },

  async signOut(deviceId?: string | null): Promise<AuthResult> {
    const supabase = createClient();

    // Forget the last-ensured marker so the next login re-registers this device
    // (unbind_push_subscription nulls user_id, so a later sync must write again).
    resetPushSubscriptionSync();

    if (deviceId) {
      await supabase.rpc("unbind_push_subscription", {
        p_device_id: deviceId,
      } as never);
    }

    const { error } = await supabase.auth.signOut({ scope: "local" });
    if (error) {
      return { success: false, error: error.message };
    }
    return { success: true };
  },

  async verifyOtp(token: string): Promise<AuthResult<{ user: User }>> {
    const email = await getPendingVerifyEmail();
    if (!email) {
      return {
        success: false,
        error: "Email not found. Please sign up again.",
      };
    }

    const parsed = otpSchema.safeParse({ email, token });
    if (!parsed.success) {
      return {
        success: false,
        error: parsed.error.issues[0]?.message ?? "Invalid OTP",
      };
    }

    const supabase = createClient();
    const { data, error } = await supabase.auth.verifyOtp({
      email: parsed.data.email,
      token: parsed.data.token,
      type: "signup",
    });

    if (error) {
      return { success: false, error: error.message };
    }

    if (data.session) {
      await clearPendingVerifyEmail();
    }

    if (!data.user) {
      return { success: false, error: "Verification failed" };
    }

    return { success: true, data: { user: data.user }, user: data.user };
  },

  async resendVerificationOtp(captchaToken?: string): Promise<AuthResult> {
    const email = await getPendingVerifyEmail();
    if (!email) {
      return {
        success: false,
        error: "Email not found. Please sign up again.",
      };
    }

    const parsed = forgotPasswordSchema.safeParse({ email });
    if (!parsed.success) {
      return {
        success: false,
        error: parsed.error.issues[0]?.message ?? "Invalid email",
      };
    }

    const supabase = createClient();
    const { error } = await supabase.auth.resend({
      type: "signup",
      email: parsed.data.email,
      ...(captchaToken ? { options: { captchaToken } } : {}),
    });

    if (error) {
      return { success: false, error: error.message };
    }
    return { success: true };
  },

  async sendPasswordResetEmail(
    email: string,
    captchaToken?: string,
  ): Promise<AuthResult> {
    const parsed = forgotPasswordSchema.safeParse({ email });
    if (!parsed.success) {
      return {
        success: false,
        error: parsed.error.issues[0]?.message ?? "Invalid email",
      };
    }

    const supabase = createClient();
    const { error } = await supabase.auth.resetPasswordForEmail(
      parsed.data.email,
      {
        redirectTo: getPasswordResetRedirectUrl(),
        ...(captchaToken ? { captchaToken } : {}),
      },
    );

    if (error) {
      return { success: false, error: error.message };
    }
    return { success: true };
  },

  async updatePassword(password: string): Promise<AuthResult> {
    const parsed = resetPasswordSchema.safeParse({ password });
    if (!parsed.success) {
      return {
        success: false,
        error: parsed.error.issues[0]?.message ?? "Invalid password",
      };
    }

    const supabase = createClient();
    const { error: updateError } = await supabase.auth.updateUser({
      password: parsed.data.password,
    });

    if (updateError) {
      return { success: false, error: updateError.message };
    }

    const { error: signOutError } = await supabase.auth.signOut({
      scope: "local",
    });
    if (signOutError) {
      return { success: false, error: signOutError.message };
    }

    clearPasswordRecoveryPending();
    return { success: true };
  },

  async sendAccountDeletionOtp(
    email: string,
    captchaToken?: string,
  ): Promise<AuthResult> {
    const parsed = forgotPasswordSchema.safeParse({ email });
    if (!parsed.success) {
      return {
        success: false,
        error: parsed.error.issues[0]?.message ?? "Invalid email",
      };
    }

    const supabase = createClient();
    const { error } = await supabase.auth.signInWithOtp({
      email: parsed.data.email,
      options: {
        shouldCreateUser: false,
        ...(captchaToken ? { captchaToken } : {}),
      },
    });

    if (error) {
      return { success: false, error: error.message };
    }
    return { success: true };
  },

  async verifyAccountDeletionOtp(
    email: string,
    token: string,
  ): Promise<AuthResult> {
    const parsed = otpSchema.safeParse({ email, token });
    if (!parsed.success) {
      return {
        success: false,
        error: parsed.error.issues[0]?.message ?? "Invalid code",
      };
    }

    const supabase = createClient();
    const { error } = await supabase.auth.verifyOtp({
      email: parsed.data.email,
      token: parsed.data.token,
      type: "email",
    });

    if (error) {
      return { success: false, error: error.message };
    }
    return { success: true };
  },

  async signInWithGoogle(returnTo?: string): Promise<AuthResult> {
    const supabase = createClient();
    void returnTo;
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: getOAuthRedirectUrl() },
    });

    if (error) {
      return { success: false, error: error.message };
    }
    return { success: true };
  },

  async exchangeOAuthCode(code: string): Promise<AuthResult<{ user: User }>> {
    return this.exchangeRecoveryCode(code);
  },

  async exchangeRecoveryCode(
    code: string,
  ): Promise<AuthResult<{ user: User }>> {
    const supabase = createClient();
    const { data, error } = await supabase.auth.exchangeCodeForSession(code);

    if (error) {
      return { success: false, error: error.message };
    }

    if (!data.user) {
      return { success: false, error: "Recovery session missing" };
    }

    return { success: true, data: { user: data.user }, user: data.user };
  },
};
